import { Alert, type AlertColor, Snackbar } from "@mui/material";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

interface SnackbarMessage {
  id: number;
  message: string;
  severity: AlertColor;
}

interface SnackbarState {
  open: boolean;
  current: SnackbarMessage | null;
  queue: SnackbarMessage[];
}

interface SnackbarContextType {
  showSnackbar: (message: string, severity?: AlertColor) => void;
}

const SnackbarContext = createContext<SnackbarContextType>({
  showSnackbar: () => {},
});

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const nextMessageIdRef = useRef(0);
  const [state, setState] = useState<SnackbarState>({
    open: false,
    current: null,
    queue: [],
  });

  const showSnackbar = useCallback(
    (message: string, _severity: AlertColor = "success") => {
      const snackbarMessage: SnackbarMessage = {
        id: ++nextMessageIdRef.current,
        message,
        severity: "success",
      };

      setState((prev) => {
        if (prev.current === null) {
          return { open: true, current: snackbarMessage, queue: prev.queue };
        }

        return { ...prev, queue: [...prev.queue, snackbarMessage] };
      });
    },
    [],
  );

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const handleExited = useCallback(() => {
    setState((prev) => {
      if (prev.queue.length === 0) {
        return { open: false, current: null, queue: [] };
      }

      const [nextMessage, ...remainingQueue] = prev.queue;
      return {
        open: true,
        current: nextMessage,
        queue: remainingQueue,
      };
    });
  }, []);

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        key={state.current?.id}
        open={state.open}
        autoHideDuration={5000}
        onClose={handleClose}
        slotProps={{
          transition: {
            onExited: handleExited,
          },
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity={state.current?.severity ?? "success"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {state.current?.message ?? ""}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar() {
  return useContext(SnackbarContext);
}
